import React from 'react'
import { Col, Row } from 'antd'
import type { GetPublicBlogPostQuery, PublicCmsItemBlogPost } from 'apollo-gql'
import prefetchBlogPost from 'graphql/prefetchers/prefetchBlogPost'
import prefetchCategory from 'graphql/prefetchers/prefetchCategory'
import { initializeApollo } from 'lib/apolloClient'
import { setCacheControlHeader } from 'lib/cache'
import { useTranslation } from 'lib/i18n'
import locationTemporaryRewrite from 'lib/locationTemporaryRewrite'
import { isServer } from 'lib/utils'
import { NextPageContext } from 'next'
import { NextSeo } from 'next-seo'

import BlogBreadcrumbs from 'components/BlogBreadcrumbs/BlogBreadcrumbs'
import BlogPost from 'components/BlogPost/BlogPost'
import ErrorMessage from 'components/ErrorMessage/ErrorMessage'
import Heading from 'components/Heading/Heading'
import NotFoundPage from 'components/NotFound/NotFoundPage'
import PublicLayout from 'components/PublicLayout/PublicLayout'
import Spinner from 'components/Spinner/Spinner'
import Wrapper from 'components/Wrapper/Wrapper'
import useIsDefaultLanguage from 'hooks/useIsDefaultLanguage'
import usePublicCategory from 'hooks/usePublicCategory'
import useRouter from 'hooks/useRouter'

import styles from './index.module.css'

function Seo({ title }: { title: string }) {
  const { t } = useTranslation()
  const isDefaultLanguage = useIsDefaultLanguage()

  const meta = {
    title: t(
      'blog.category.meta.title',
      'Discover our most popular {{title}} articles - memmo.me',
      { title },
    ),
    description: t(
      'blog.category.meta.description',
      'memmo.me makes it easy to book personalised video messages from celebrities. Click here to get inspiration around {{title}}',
      { title },
    ),
  }

  return (
    <NextSeo
      title={meta.title}
      description={meta.description}
      openGraph={meta}
      noindex={!isDefaultLanguage}
      nofollow={!isDefaultLanguage}
    />
  )
}

function BlogCategoryPageNotFound(): JSX.Element {
  const { t } = useTranslation()

  return (
    <NotFoundPage
      title={t('blog.category.404.title', 'Blog category not found')}
      subTitle={t(
        'blog.category.404.subTitle',
        'Sorry, the blog category you were looking for does not exist.',
      )}
      backButtonText={t('blog.category.404.goBack', 'Check out other posts')}
      backButtonHref="/blog"
    />
  )
}

function BlogCategoryPage(): JSX.Element {
  const { category: queryCategory } = useRouter().query
  const { category, loading, error } = usePublicCategory(
    queryCategory as string,
  )

  if (!isServer() && loading) {
    return (
      <PublicLayout bottom={false}>
        <Spinner centered />
      </PublicLayout>
    )
  }

  if (error) {
    return <ErrorMessage message="Error loading blog category" />
  }

  if (!category) {
    return <BlogCategoryPageNotFound />
  }

  return (
    <PublicLayout bottom={false}>
      <Seo title={category.title} />
      <Wrapper>
        <Row gutter={24}>
          <Col
            xs={24}
            sm={{ span: 15 }}
            md={{ span: 16 }}
            lg={{ span: 20, offset: 2 }}
          >
            <Heading level="h2">
              <h1 data-cy="blogCategoryTitle" className={styles.title}>
                {category.title}
              </h1>
            </Heading>

            <BlogBreadcrumbs category={category} />

            <Row gutter={24}>
              {category.cmsItems.map((item: PublicCmsItemBlogPost) => (
                <BlogPost
                  id={item.id}
                  key={item.id}
                  title={item.content.title}
                  slug={item.slug}
                  image={item.image?.url}
                  excerpt={item.excerpt}
                  categorySlug={category.slug}
                />
              ))}
            </Row>
          </Col>
        </Row>
      </Wrapper>
    </PublicLayout>
  )
}

const redirectTo = (
  blogPost: GetPublicBlogPostQuery['market']['blogPost'],
  ctx: NextPageContext,
) => {
  const { category, slug } = blogPost
  const { query, res, req } = ctx

  locationTemporaryRewrite(
    res,
    req.url.replace(query.category as string, [category.slug, slug].join('/')),
  )
}

BlogCategoryPage.getInitialProps = async (ctx: NextPageContext) => {
  if (!isServer()) {
    return {}
  }

  setCacheControlHeader({ ctx, ttl: 1800 })
  const apolloClient = initializeApollo(ctx)
  const { query } = ctx

  const category = await prefetchCategory(apolloClient, {
    slug: query.category,
  })

  if (!category) {
    const blogPost = await prefetchBlogPost(apolloClient, {
      slug: query.category,
    })

    if (blogPost) {
      redirectTo(blogPost, ctx)
    }
  }

  return {
    initialApolloState: apolloClient.cache.extract(),
  }
}

export default BlogCategoryPage
